/**
 * Keeps the Android app awake while a video upload is in flight, through the
 * shell's `UploadKeepAwake` plugin (Strength-Labs/TurnkeyCoach_Capacitor,
 * docs/UploadKeepAwake.md).
 *
 * WHY. Most of a video upload is page work: the WebCodecs compression, the MD5,
 * the presign, the temp-file copy and saving the video afterwards. Only the S3
 * PUT itself is handed to the phone. When the client switches apps, Android
 * freezes the app about ten seconds later, and even with the app kept alive
 * Chromium freezes a hidden page after about a minute. So the upload paused
 * until the client came back (client report, 2026-10-19, Galaxy S23).
 *
 * The plugin runs a foreground service with an ongoing notification while it
 * is on, which stops Android freezing the app, and keeps the page counted as
 * on screen, which stops Chromium freezing it. This module turns it on when the
 * first VIDEO upload begins and off after the last one ends
 * (utils/upload_activity.js). Other uploads are left out: photos and files
 * finish in seconds, and a notification flashing up for each would be noise.
 *
 * THREE RULES, each for a way it would otherwise fail:
 *   - The stop waits STOP_DELAY_MS. Uploads often run one after another (the
 *     workout queue, the offline sync on reconnect), with a gap between them.
 *     Stopping in that gap, in the background, lets the page freeze, and
 *     Android refuses to start the service again from the background, so
 *     every video after the first would stall.
 *   - Calls go to the plugin one at a time, and each goes for whatever is
 *     wanted by the time it runs, so a quick off-on never reaches the phone
 *     as a stop after a start.
 *   - A start Android refused (`{ active: false }`, because the app was
 *     already in the background) is tried again when the page is next
 *     visible, which means the client is back in the app.
 *
 * No-op outside the Capacitor shell, and on app builds without the plugin
 * (iOS, and Android builds before it shipped). Never throws.
 */

import Honeybadger from "@honeybadger-io/js";
import { isCapacitorPluginAvailable } from "./detection";
import {
  hasActiveUploads,
  onUploadActivityChange,
} from "../utils/upload_activity";

// The plugin's registerPlugin() name in the shell. The gate below depends on
// it matching exactly, or the feature silently stays off.
const PLUGIN_NAME = "UploadKeepAwake";

// Shown in the ongoing notification. Short, because a notification line is.
export const KEEP_AWAKE_NOTIFICATION = Object.freeze({
  title: "Uploading your video",
  body: "You can switch apps. We'll keep going.",
});

// Long enough to bridge the gap between two queued uploads (an IndexedDB read,
// a presign), short enough that the notification does not outstay the upload.
export const STOP_DELAY_MS = 5_000;

let registered = false;
// What the uploads want, and what the plugin last said it is (null until the
// first answer, so the startup call always goes out).
let wanted = false;
let reported = null;
let queue = Promise.resolve();
let stopTimer = null;

function reportKeepAwakeError(error, action) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_upload_keep_awake", action },
    });
  } catch {
    // reporting must never break an upload
  }
}

function getPlugin() {
  try {
    return window.Capacitor?.Plugins?.[PLUGIN_NAME] ?? null;
  } catch {
    return null;
  }
}

async function callPlugin(method, args) {
  const plugin = getPlugin();
  if (!plugin || typeof plugin[method] !== "function") return null;
  try {
    return await plugin[method](args);
  } catch (error) {
    reportKeepAwakeError(error, method);
    return null;
  }
}

// One call at a time, each for the state wanted when it runs.
function settle() {
  queue = queue.then(async () => {
    const target = wanted;
    if (reported === target) return;
    if (target) {
      const result = await callPlugin("start", KEEP_AWAKE_NOTIFICATION);
      reported = result?.active === true;
    } else {
      await callPlugin("stop");
      reported = false;
    }
  });
  return queue;
}

function onVideoUploadsChange(active) {
  wanted = active;
  if (active) {
    clearTimeout(stopTimer);
    stopTimer = null;
    settle();
    return;
  }
  clearTimeout(stopTimer);
  stopTimer = setTimeout(() => {
    stopTimer = null;
    if (!wanted) settle();
  }, STOP_DELAY_MS);
}

function onVisibilityChange() {
  if (document.visibilityState === "visible" && wanted && reported === false) {
    settle();
  }
}

/**
 * Start following video uploads. Runs once; no-op without the plugin.
 */
export function registerUploadKeepAwake() {
  if (registered || !isCapacitorPluginAvailable(PLUGIN_NAME)) {
    return;
  }
  registered = true;
  // A previous life of this page may have left it on: a reload mid-upload
  // throws the upload away, and the service would otherwise run on until its
  // own time limit. So the first call goes out at once, whichever way: off,
  // unless an upload began before this ran.
  wanted = hasActiveUploads("video");
  settle();
  onUploadActivityChange(onVideoUploadsChange, { kind: "video" });
  document.addEventListener("visibilitychange", onVisibilityChange);
}

/**
 * For upload telemetry: null where there is no keep-awake (a browser, iOS, an
 * app build without the plugin), otherwise whether the plugin last said it was
 * on. False while uploading means Android refused it.
 * @returns {boolean|null}
 */
export function keepAwakeState() {
  return registered ? reported === true : null;
}

/** Test-only: wait for the plugin calls queued so far. */
export function __keepAwakeSettledForTests() {
  return queue;
}
